<script setup>
import ArrowsLeftRightIcon from '@iconify-vue/ph/arrows-left-right';
import EyeIcon from '@iconify-vue/ph/eye';
import FloppyDiskIcon from '@iconify-vue/ph/floppy-disk';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DialogModal from '@js/components/dialogs/DialogModal.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import TextareaField from '@js/components/fields/TextareaField.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import { toastError, toastSaved } from '@js/utils/toast';
import { computed, ref, useId, watch } from 'vue';

const {
    leftContent,
    rightContent,
    leftLabel = 'Original',
    rightLabel = 'Générée',
    length = 60,
    url,
    fieldName = 'value',
    payload = {},
    richText = false,
    maxLength = null,
    callback,
} = defineProps({
    leftContent: {
        type: String,
        required: false,
        default: null,
    },
    rightContent: {
        type: String,
        required: false,
        default: null,
    },
    leftLabel: {
        type: String,
        required: false,
    },
    rightLabel: {
        type: String,
        required: false,
    },
    length: {
        type: Number,
        required: false,
    },
    url: {
        type: String,
        required: false,
    },
    fieldName: {
        type: String,
        required: false,
    },
    payload: {
        type: Object,
        required: false,
    },
    richText: {
        type: Boolean,
        required: false,
    },
    maxLength: {
        type: Number,
        required: false,
    },
    callback: {
        type: Function,
        required: false,
    },
});

const emit = defineEmits(['saved']);

const fieldId = `content-comparison-${useId()}`;

const isOpen = ref(false);

const isEditing = ref(false);

const isSaving = ref(false);

const editedContent = ref('');

const savedContent = ref(null);

const generatedContent = computed(() => savedContent.value ?? rightContent);

const isEditable = computed(() => !!url);

const hasGenerated = computed(() => !!generatedContent.value);

const hasContent = computed(() => !!leftContent || hasGenerated.value);

const displayContent = computed(
    () => generatedContent.value || leftContent || '',
);

const editorLegend = computed(() =>
    maxLength
        ? `${(editedContent.value || '').length} / ${maxLength} caractères`
        : undefined,
);

watch(
    () => rightContent,
    () => {
        savedContent.value = null;
    },
);

watch(isOpen, (opened) => {
    if (!opened) {
        isEditing.value = false;
    }
});

function startEdit() {
    editedContent.value = generatedContent.value || '';
    isEditing.value = true;
}

function cancelEdit() {
    isEditing.value = false;
}

function save() {
    isSaving.value = true;

    axios
        .post(url, {
            ...payload,
            [fieldName]: editedContent.value,
        })
        .then((response) => {
            savedContent.value = editedContent.value;
            isEditing.value = false;

            toastSaved();

            emit('saved', editedContent.value);

            if (callback) {
                callback(editedContent.value, response);
            }
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(() => {
            isSaving.value = false;
        });
}

function stripHtml(html) {
    if (!html) {
        return '';
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
}

function truncate(text, textLength) {
    if (!text || text.length <= textLength) {
        return text || '';
    }

    return text.substring(0, textLength) + '…';
}
</script>

<template>
    <BaseCell>
        <span
            class="text-sm"
            :class="{
                'cursor-pointer hover:text-indigo-600':
                    hasContent || isEditable,
            }"
            @click="hasContent || isEditable ? (isOpen = true) : null"
        >
            {{ truncate(stripHtml(displayContent), length) }}
        </span>

        <DialogModal
            v-model="isOpen"
            :size="hasGenerated || isEditable ? 'large' : 'small'"
        >
            <template #trigger>
                <ActionButton
                    v-if="hasGenerated"
                    :title="
                        isEditable
                            ? 'Comparer et modifier le contenu'
                            : 'Comparer avant / après'
                    "
                    :icon="ArrowsLeftRightIcon"
                    size="small"
                    square
                    type="secondary"
                />

                <ActionButton
                    v-else-if="isEditable"
                    title="Modifier le contenu"
                    :icon="PencilSimpleIcon"
                    size="small"
                    square
                    type="secondary"
                />

                <ActionButton
                    v-else-if="hasContent"
                    title="Voir le contenu"
                    :icon="EyeIcon"
                    size="small"
                    square
                    type="secondary"
                />
            </template>

            <template #title>
                {{
                    hasGenerated || isEditable
                        ? 'Comparaison de contenu'
                        : 'Contenu actuel'
                }}
            </template>

            <template v-if="hasGenerated || isEditable" #full-content>
                <div class="grid grid-cols-2 gap-0">
                    <div class="border-r border-gray-200 p-5">
                        <div class="mb-3 flex h-8 items-center gap-2">
                            <span
                                class="rounded-full bg-gray-500 px-3 py-1 text-xs font-medium text-white"
                            >
                                {{ leftLabel }}
                            </span>
                        </div>
                        <div
                            v-if="leftContent"
                            class="wysiwyg prose prose-sm max-w-none text-sm text-gray-700"
                            v-html="leftContent"
                        ></div>
                        <p v-else class="text-sm text-gray-400 italic">
                            Aucun contenu
                        </p>
                    </div>

                    <div class="p-5">
                        <div
                            class="mb-3 flex h-8 items-center justify-between gap-2"
                        >
                            <span
                                class="rounded-full bg-emerald-600 px-3 py-1 text-xs font-medium text-white"
                            >
                                {{ rightLabel }}
                            </span>

                            <div
                                v-if="isEditable"
                                class="flex items-center gap-2"
                            >
                                <template v-if="isEditing">
                                    <ActionButton
                                        type="secondary"
                                        size="small"
                                        :callback="cancelEdit"
                                        :loading="isSaving"
                                    >
                                        Annuler
                                    </ActionButton>

                                    <ActionButton
                                        type="primary"
                                        size="small"
                                        :left-icon="FloppyDiskIcon"
                                        :callback="save"
                                        :loading="isSaving"
                                    >
                                        Enregistrer
                                    </ActionButton>
                                </template>

                                <ActionButton
                                    v-else
                                    type="secondary"
                                    size="small"
                                    :left-icon="PencilSimpleIcon"
                                    :callback="startEdit"
                                >
                                    Modifier
                                </ActionButton>
                            </div>
                        </div>

                        <template v-if="isEditing">
                            <EditorField
                                v-if="richText"
                                v-model="editedContent"
                                :field-id="fieldId"
                                :field-name="fieldId"
                                :disabled="isSaving"
                            />

                            <TextareaField
                                v-else
                                v-model="editedContent"
                                :field-id="fieldId"
                                :field-name="fieldId"
                                :rows="4"
                                :disabled="isSaving"
                                :legend="editorLegend"
                                resize="vertical"
                            />
                        </template>

                        <div
                            v-else-if="hasGenerated"
                            class="wysiwyg prose prose-sm max-w-none text-sm text-gray-700"
                            v-html="generatedContent"
                        ></div>

                        <p v-else class="text-sm text-gray-400 italic">
                            Aucun contenu
                        </p>
                    </div>
                </div>
            </template>

            <template v-else #content>
                <div
                    v-if="leftContent"
                    class="wysiwyg prose prose-sm max-w-none text-sm text-gray-700"
                    v-html="leftContent"
                ></div>
                <p v-else class="text-sm text-gray-400 italic">Aucun contenu</p>
            </template>
        </DialogModal>
    </BaseCell>
</template>
